import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "pcard__media" }
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = {
  key: 2,
  class: "pcard__soldout"
}
const _hoisted_4 = { class: "pcard__info" }
const _hoisted_5 = { class: "pcard__meta" }
const _hoisted_6 = { class: "pcard__rating" }
const _hoisted_7 = { class: "pcard__sold" }
const _hoisted_8 = { class: "pcard__name" }
const _hoisted_9 = { class: "pcard__price-row" }
const _hoisted_10 = { class: "pcard__amount" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "pcard__bottom" }
const _hoisted_14 = { class: "pcard__review" }
const _hoisted_15 = {
  key: 1,
  class: "pcard__link pcard__link--preview",
  "aria-label": "몰 목록 카드 미리보기"
}
const _hoisted_16 = ["src", "alt"]
const _hoisted_17 = {
  key: 1,
  class: "pcard__img-empty"
}
const _hoisted_18 = {
  key: 4,
  class: "pcard__soldout"
}
const _hoisted_19 = { class: "pcard__info" }
const _hoisted_20 = {
  class: "pcard__meta pcard__meta--muted",
  title: "판매·평점은 주문·후기 후 반영됩니다"
}
const _hoisted_21 = { class: "pcard__rating" }
const _hoisted_22 = { class: "pcard__sold" }
const _hoisted_23 = { class: "pcard__name" }
const _hoisted_24 = { class: "pcard__price-row" }
const _hoisted_25 = { class: "pcard__amount" }
const _hoisted_26 = { key: 0 }
const _hoisted_27 = { key: 1 }
const _hoisted_28 = { class: "pcard__bottom" }
const _hoisted_29 = {
  class: "pcard__review pcard__meta--muted",
  title: "후기는 구매 후 쌓입니다"
}
const _hoisted_30 = ["aria-label", "disabled"]
const _hoisted_31 = ["disabled"]
const _hoisted_32 = ["disabled"]

import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import MallOrderPackMark from '@/components/mall/MallOrderPackMark.vue'
import { type MallProduct, formatPrice } from '@/constants/mallProducts'
import type { ProductPreviewZone } from '@/constants/productEditor'
import { useCart } from '@/composables/useCart'
import { useMallAuth } from '@/composables/useMallAuth'
import { useMallToast } from '@/composables/useMallToast'
import { useWishlist } from '@/composables/useWishlist'
import { calcMallUnitPrice } from '@/utils/mallPricing'
import { formatStockCount, isOrderPack, sellUnitNoun } from '@/utils/mallSell'
import { shareMallProduct } from '@/utils/mallShare'


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductCard',
  props: {
    product: {},
    mode: { default: 'default' },
    activeZone: { default: '' }
  },
  emits: ["edit-zone"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const isPreview = computed(() => props.mode === 'preview')

const router = useRouter()
const { isWished, toggleWish } = useWishlist()
const { addItem } = useCart()
const { ensureLoggedIn } = useMallAuth()
const { showSuccess, showError } = useMallToast()
const cartBusy = ref(false)

function zoneOn(zone: ProductPreviewZone): boolean {
  return props.activeZone === zone
}

function emitEdit(zone: ProductPreviewZone, fieldSuffix?: string) {
  emit('edit-zone', zone, fieldSuffix)
}

const onWish = () => {
  if (isPreview.value) return
  if (!ensureLoggedIn()) return
  toggleWish(props.product.id)
}

const onCart = async () => {
  if (isPreview.value) return
  if (!ensureLoggedIn() || props.product.soldOut || cartBusy.value) return
  if ((props.product.options || []).length) {
    await router.push(`/mall/${props.product.id}`)
    return
  }
  cartBusy.value = true
  try {
    await addItem(props.product.id, Math.max(1, props.product.minOrder || 1))
    showSuccess('장바구니에 담았습니다.')
  } catch (error) {
    showError(error)
  } finally {
    cartBusy.value = false
  }
}

const onShare = async () => {
  if (isPreview.value) return
  const url = `${window.location.origin}/mall/${props.product.id}`
  const result = await shareMallProduct({
    title: props.product.name,
    text: `${props.product.name} — 함우리몰`,
    url,
  })
  if (result === 'copied') showSuccess('상품 주소를 복사했습니다.')
}

const priced = computed(() =>
  calcMallUnitPrice({
    listPrice: props.product.listPrice,
    salePrice: props.product.price,
  }),
)

const wished = computed(() => isWished(props.product.id))
const stockQty = computed(() => Math.max(0, Number(props.product.stock) || 0))
const sellNoun = computed(() => sellUnitNoun(props.product.sellUnit))
const isGroupBuy = computed(() => props.product.mallKind === 'SALE')
const isPack = computed(() => isOrderPack(props.product.orderStep))
const stockLabel = computed(() => {
  if (props.product.soldOut || !(stockQty.value > 0)) return '품절'
  return `재고 ${formatStockCount(stockQty.value, props.product.sellUnit)}`
})

const badgeClass = computed(() => {
  if (props.product.badge === '특가') return 'pcard__badge--urgent'
  if (props.product.badge === 'NEW') return 'pcard__badge--new'
  if (props.product.badge === '베스트') return 'pcard__badge--best'
  return 'pcard__badge--hot'
})

function formatSold(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}만`
  return value.toLocaleString('ko-KR')
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("article", {
    class: _normalizeClass(["pcard", {
      'pcard--preview': isPreview.value,
      [`pcard--hl-${__props.activeZone}`]: isPreview.value && __props.activeZone,
    }])
  }, [
    (!isPreview.value)
      ? (_openBlock(), _createBlock(_component_router_link, {
          key: 0,
          to: `/mall/${__props.product.id}`,
          class: "pcard__link",
          "aria-label": `${__props.product.name}, ${isGroupBuy.value ? '1개당 ' : ''}${_unref(formatPrice)(priced.value.unitPrice)}원`
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("img", {
                class: "pcard__img",
                src: __props.product.image,
                alt: `${__props.product.name} 상품 이미지`,
                loading: "lazy",
                decoding: "async"
              }, null, 8, _hoisted_2),
              (__props.product.badge)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass(["pcard__badge", badgeClass.value])
                  }, _toDisplayString(__props.product.badge), 3))
                : _createCommentVNode("", true),
              (isPack.value && !isGroupBuy.value)
                ? (_openBlock(), _createBlock(MallOrderPackMark, {
                    key: 1,
                    size: "card",
                    "order-step": __props.product.orderStep || 1,
                    "sell-unit": __props.product.sellUnit
                  }, null, 8, ["order-step", "sell-unit"]))
                : _createCommentVNode("", true),
              (__props.product.soldOut)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, "품절"))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("p", _hoisted_5, [
                _createElementVNode("span", _hoisted_6, [
                  _createVNode(AppIcon, {
                    icon: "mdi-star",
                    size: 14
                  }),
                  _createTextVNode(" " + _toDisplayString(__props.product.rating.toFixed(1)), 1)
                ]),
                _createElementVNode("span", _hoisted_7, _toDisplayString(formatSold(__props.product.soldCount)) + "개 구매", 1)
              ]),
              _createElementVNode("h3", _hoisted_8, _toDisplayString(__props.product.name), 1),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("span", _hoisted_10, [
                  (isGroupBuy.value)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_11, "1개당"))
                    : _createCommentVNode("", true),
                  _createTextVNode(_toDisplayString(_unref(formatPrice)(priced.value.unitPrice)), 1),
                  _cache[5] || (_cache[5] = _createElementVNode("em", null, "원", -1)),
                  (!isGroupBuy.value && sellNoun.value === '박스')
                    ? (_openBlock(), _createElementBlock("small", _hoisted_12, "/박스"))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("span", _hoisted_14, "구매후기 " + _toDisplayString(formatSold(__props.product.reviewCount)), 1),
                _createElementVNode("span", {
                  class: _normalizeClass(["pcard__stock", { 'is-out': __props.product.soldOut || !(stockQty.value > 0) }])
                }, _toDisplayString(stockLabel.value), 3)
              ])
            ])
          ]),
          _: 1
        }, 8, ["to", "aria-label"]))
      : (_openBlock(), _createElementBlock("div", _hoisted_15, [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["pcard__hit pcard__media", { 'is-on': zoneOn('gallery') || zoneOn('badge') }]),
            title: "이미지 · 배지 수정",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (emitEdit('gallery')))
          }, [
            _cache[6] || (_cache[6] = _createElementVNode("span", { class: "pcard__hit-label" }, "이미지 · 배지", -1)),
            (__props.product.image)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  class: "pcard__img",
                  src: __props.product.image,
                  alt: `${__props.product.name} 상품 이미지`
                }, null, 8, _hoisted_16))
              : (_openBlock(), _createElementBlock("span", _hoisted_17, "이미지 없음")),
            (__props.product.badge)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 2,
                  class: _normalizeClass(["pcard__badge", [badgeClass.value, { 'is-on': zoneOn('badge') }]]),
                  onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (emitEdit('badge')), ["stop"]))
                }, _toDisplayString(__props.product.badge), 3))
              : _createCommentVNode("", true),
            (isPack.value && !isGroupBuy.value)
              ? (_openBlock(), _createBlock(MallOrderPackMark, {
                  key: 3,
                  size: "card",
                  "order-step": __props.product.orderStep || 1,
                  "sell-unit": __props.product.sellUnit
                }, null, 8, ["order-step", "sell-unit"]))
              : _createCommentVNode("", true),
            (__props.product.soldOut)
              ? (_openBlock(), _createElementBlock("span", _hoisted_18, "품절"))
              : _createCommentVNode("", true)
          ], 2),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("p", _hoisted_20, [
              _createElementVNode("span", _hoisted_21, [
                _createVNode(AppIcon, {
                  icon: "mdi-star",
                  size: 14
                }),
                _createTextVNode(" " + _toDisplayString(__props.product.rating.toFixed(1)), 1)
              ]),
              _createElementVNode("span", _hoisted_22, _toDisplayString(formatSold(__props.product.soldCount)) + "개 구매", 1)
            ]),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["pcard__hit pcard__hit--block", { 'is-on': zoneOn('name') }]),
              title: "상품명 수정",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (emitEdit('name')))
            }, [
              _cache[7] || (_cache[7] = _createElementVNode("span", { class: "pcard__hit-label" }, "상품명", -1)),
              _createElementVNode("h3", _hoisted_23, _toDisplayString(__props.product.name || '상품명 없음'), 1)
            ], 2),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["pcard__hit pcard__hit--block", { 'is-on': zoneOn('price') }]),
              title: "가격 수정",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (emitEdit('price')))
            }, [
              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "pcard__hit-label" }, "가격", -1)),
              _createElementVNode("div", _hoisted_24, [
                _createElementVNode("span", _hoisted_25, [
                  (isGroupBuy.value)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_26, "1개당"))
                    : _createCommentVNode("", true),
                  _createTextVNode(_toDisplayString(_unref(formatPrice)(priced.value.unitPrice)), 1),
                  _cache[8] || (_cache[8] = _createElementVNode("em", null, "원", -1)),
                  (!isGroupBuy.value && sellNoun.value === '박스')
                    ? (_openBlock(), _createElementBlock("small", _hoisted_27, "/박스"))
                    : _createCommentVNode("", true)
                ])
              ])
            ], 2),
            _createElementVNode("div", _hoisted_28, [
              _createElementVNode("span", _hoisted_29, " 구매후기 " + _toDisplayString(formatSold(__props.product.reviewCount)), 1),
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["pcard__hit pcard__stock", {
              'is-out': __props.product.soldOut || !(stockQty.value > 0),
              'is-on': zoneOn('stock'),
            }]),
                title: "재고 수정",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (emitEdit('stock')))
              }, [
                _cache[10] || (_cache[10] = _createElementVNode("span", { class: "pcard__hit-label" }, "재고", -1)),
                _createTextVNode(" " + _toDisplayString(stockLabel.value), 1)
              ], 2)
            ])
          ])
        ])),
    _createElementVNode("div", {
      class: _normalizeClass(["pcard__actions", { 'is-preview': isPreview.value }])
    }, [
      _createElementVNode("button", {
        type: "button",
        class: _normalizeClass(["pcard__tool", { 'is-on': wished.value }]),
        "aria-label": wished.value ? '찜 해제' : '찜하기',
        disabled: isPreview.value,
        onClick: onWish
      }, [
        _createVNode(AppIcon, {
          icon: wished.value ? 'mdi-heart' : 'mdi-heart-outline',
          size: 18
        }, null, 8, ["icon"])
      ], 10, _hoisted_30),
      _createElementVNode("button", {
        type: "button",
        class: "pcard__tool",
        "aria-label": "공유하기",
        disabled: isPreview.value,
        onClick: onShare
      }, [
        _createVNode(AppIcon, {
          icon: "mdi-share-variant-outline",
          size: 18
        })
      ], 8, _hoisted_31),
      _createElementVNode("button", {
        type: "button",
        class: "pcard__cart",
        disabled: isPreview.value || __props.product.soldOut || cartBusy.value,
        onClick: onCart
      }, [
        _createVNode(AppIcon, {
          icon: "mdi-cart-outline",
          size: 16
        }),
        _createTextVNode(" " + _toDisplayString(__props.product.soldOut ? '품절' : cartBusy.value ? '담는중' : '장바구니'), 1)
      ], 8, _hoisted_32)
    ], 2)
  ], 2))
}
}

})